/**
 * The mobile ApiClient (reused: fetch, timeout, ApiError / NetworkError, field login) plus the
 * polling-day endpoints.
 */
import type { SurveyAnswer } from "./survey";
import { ApiClient } from "@core/api";
import type {
  RallyCheck, RallyKind, RallyStatus, SosCategory, TourPurpose, TourStatus, TripStatus, TurnoutSlot, VehicleKind, VehicleStatus,
} from "@npac/rules";
import type { Assembly, User } from "@core/types";

/** The API's `user` (email, several assemblies, nullable phone); the mobile core type now matches it. */
export type FieldUser = User;

export interface AssemblyInfo extends Assembly {
  nameHi?: string | null;
  /** module locks (N-PAC's subscription): null = every module open; a list = only these */
  modules?: string[] | null;
}

export interface SosBody {
  boothCode: string;
  category: SosCategory;
  /** required: made once per alert on the phone; a retry with the same id never raises a second alert */
  clientId: string;
  note?: string;
  lat?: number;
  lng?: number;
  accuracyM?: number;
  /** optional photo, compressed on the phone (JPEG/PNG/WebP, base64, at most 1.5 MB) */
  photo?: SosPhoto;
}

export interface SosPhoto {
  type: "image/jpeg" | "image/png" | "image/webp";
  /** base64 without the data: prefix */
  data: string;
}

export type SosStatus = "open" | "acknowledged" | "resolved";

export interface Sos {
  id: number;
  boothCode: string;
  boothName: string;
  panchayat?: string | null;
  category: SosCategory;
  note?: string | null;
  status: SosStatus;
  raisedAt: string;
  raisedBy?: string | null;
  acknowledgedAt?: string | null;
  acknowledgedBy?: string | null;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNote?: string | null;
  location?: { lat: number; lng: number; fromBooth?: boolean } | null;
  hasPhoto?: boolean;
}

/** `location_ignored`: the GPS point was outside India / too inaccurate; the alert was saved without it. */
export interface SosReply {
  sos: Sos;
  duplicate: boolean;
  warning?: "location_ignored" | string;
}

export interface TurnoutBody {
  slot: TurnoutSlot;
  date?: string;
  votesPolled?: number;
  pct?: number;
  /** our party's votes so far (agent's own count / estimate); never more than votesPolled */
  ourVotes?: number;
}

export interface Turnout {
  date: string;
  slot: TurnoutSlot;
  votesPolled: number | null;
  pct: number | null;
  reportedAt: string;
  totalVoters: number | null;
  ourVotes?: number | null;
  ourPct?: number | null;
  /** saved, but no %: the booth has no total voters yet, or more votes than its (old) total */
  warning?: "no_total_voters" | "more_than_total_voters" | string;
}

/** Booth on a polling-day worker's list, as POST /auth/dday returns it. */
export interface DdayBooth {
  code: string;
  no: number;
  name: string;
  panchayat?: string | null;
  totalVoters?: number | null;
}

export interface DdayLoginReply {
  token: string;
  user: FieldUser;
  /** every booth of the assembly (the link's booth first) */
  booths: DdayBooth[];
  /** the booth the link named, if any */
  picked?: string | null;
  assembly: { id: string; name: string; nameHi: string | null };
}

/* ---------------- past result typed by hand ---------------- */

export interface BoothResultReply {
  election: { year: number; kind: string };
  /** the booth already has a result (the phone never gets the votes back) */
  filled?: boolean;
  candidates: { id: number; name: string; party: string | null; isAggregate: boolean; votes: number | null }[];
}

/* ---------------- campaign (rallies, tour, vehicles) ---------------- */

export interface Rally {
  id: number;
  title: string;
  kind: RallyKind;
  startsAt: string;
  place: string;
  boothCode: string | null;
  panchayat: string | null;
  chiefGuest: string | null;
  expectedCrowd: number | null;
  actualCrowd: number | null;
  inchargeName: string | null;
  inchargePhone: string | null;
  status: RallyStatus;
  permissionNo: string | null;
  checklist: Partial<Record<RallyCheck, boolean>>;
  budget: number | null;
  cost: number | null;
  notes: string | null;
}
export interface TourStop {
  id: number;
  day: string;
  atTime: string | null;
  seq: number;
  place: string;
  boothCode: string | null;
  panchayat: string | null;
  purpose: TourPurpose;
  contactName: string | null;
  contactPhone: string | null;
  status: TourStatus;
  notes: string | null;
}
export interface Trip {
  id: number;
  vehicleId: number;
  day: string;
  area: string;
  boothCodes: string[];
  startKm: number | null;
  endKm: number | null;
  fuelLitres: number | null;
  fuelCost: number | null;
  /** रुपए ड्राइवर को दिए (the day's cash) */
  cashGiven?: number | null;
  status: TripStatus;
  notes: string | null;
}
export interface Vehicle {
  id: number;
  regNo: string;
  kind: VehicleKind;
  driverName: string | null;
  driverPhone: string | null;
  status: VehicleStatus;
  equipment: string | null;
  today: Trip | null;
}
export type CampaignPart = "rallies" | "tour" | "vehicles" | "trips";

/* ---------------- driver duty (GPS) ---------------- */
export interface DriveSession {
  id: number;
  clientId: string;
  startedAt: string;
  endedAt: string | null;
  km: number;
  points: number;
  lastAt: string | null;
}
export interface DriveInfo {
  vehicle: { id: number; regNo: string; kind: VehicleKind; use: "campaign" | "candidate"; driverName: string | null };
  session: DriveSession | null;
  today: { day: string; km: number; duties: number; places: number; booths: number };
  plan: { tripId: number; area: string; status: TripStatus; boothCodes: string[]; places: { id: number; kind: string; name: string; boothCode: string; reached: boolean }[] } | null;
}
export interface DrivePoint {
  id: string;
  at: string;
  lat: number;
  lng: number;
  acc?: number | null;
  speed?: number | null;
}
export interface DrivePointsReply {
  accepted: number;
  duplicates: number;
  rejected: number;
  session: DriveSession;
  reached: { target: "place" | "booth"; id: number; name: string }[];
}
export interface DriveLoginReply {
  token: string;
  user: FieldUser;
  vehicle: { id: number; regNo: string; kind: VehicleKind; use: "campaign" | "candidate" };
  assembly: { id: string; name: string; nameHi: string | null };
}

export class FieldApi extends ApiClient {
  /** The assembly's booths, for a polling-day worker to pick theirs; the link's key is the only credential. */
  ddayBooths(assemblyId: string, key: string): Promise<{ assembly: { id: string; name: string; nameHi: string | null }; booths: DdayBooth[] }> {
    return this.request("POST", "/auth/dday/booths", { assemblyId, key }, false);
  }

  /**
   * Polling-day login by the war room's link alone (no number, no passcode): the link's key + the
   * booth the worker picks; name and mobile are optional.
   */
  ddayLogin(assemblyId: string, key: string, booth?: string | null, name?: string | null, mobile?: string | null): Promise<DdayLoginReply> {
    return this.request("POST", "/auth/dday", { assemblyId, key, booth: booth || null, name: name?.trim() || null, mobile: mobile?.trim() || null }, false);
  }

  /** exit poll (survey team): the plan's choices, sending answers, own count today */
  exitPlan(assemblyId: string): Promise<{ plan: ExitPlanInfo }> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/exit-poll/plan`);
  }
  exitSend(assemblyId: string, items: ExitAnswer[]): Promise<{ saved: { clientId: string; status: string; flags: string[] }[] }> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/exit-poll/responses`, { items });
  }
  exitMine(assemblyId: string): Promise<{ today: number; review: number }> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/exit-poll/mine`);
  }

  /** वर्तमान सर्वे (door to door / telecaller): sending answers, own record */
  surveySend(assemblyId: string, items: SurveyAnswer[]): Promise<{ saved: SurveySaved[] }> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/survey/responses`, { items });
  }
  surveyMine(assemblyId: string): Promise<SurveyMine> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/survey/mine`);
  }

  /** the booth's बस्ती / markets / crowded spots (online) */
  /** this surveyor's points and place on the leaderboard (7 days) */
  myPoints(assemblyId: string): Promise<{ me: { points: number; rank: number | null } | null; of: number }> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/leaderboard?days=7`);
  }

  /** every place of the assembly, booth by booth (the places duty / campaign duties / admins) */
  areaPlaces(assemblyId: string): Promise<AreaPlaces> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/places`);
  }
  boothPlaces(assemblyId: string, code: string): Promise<{ items: BoothPlace[]; densestId: number | null }> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/places`);
  }

  saveBoothPlace(assemblyId: string, code: string, body: BoothPlaceInput, id?: number): Promise<{ item: BoothPlace }> {
    const base = `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/places`;
    return id ? this.request("PATCH", `${base}/${id}`, body) : this.request("POST", base, body);
  }

  boothResult(assemblyId: string, code: string): Promise<BoothResultReply> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/result`);
  }

  saveBoothResult(assemblyId: string, code: string, votes: { candidateId: number; votes: number }[]): Promise<unknown> {
    return this.request("PUT", `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/result`, { votes });
  }

  campaign<T>(assemblyId: string, part: CampaignPart, day?: string): Promise<T> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/campaign/${part}${day ? `?day=${encodeURIComponent(day)}` : ""}`);
  }

  campaignSave<T>(assemblyId: string, part: CampaignPart, body: object, id?: number): Promise<{ item: T }> {
    const base = `/assemblies/${encodeURIComponent(assemblyId)}/campaign/${part}`;
    return id == null ? this.request("POST", base, body) : this.request("PATCH", `${base}/${id}`, body);
  }

  /* उपस्थिति (QR): the ID-card scanner (N-PAC, 4 Nov 2026) */
  attendanceMark(assemblyId: string, body: { event: { kind: "rally" | "tour" | "meeting"; key: string }; code?: string; phone?: string; name?: string }) {
    return this.request<{ duplicate: boolean; total: number; person: { name: string; designation: string | null; role: string; booth: string | null; boothName: string | null; phone: string; at: string } }>(
      "POST",
      `/assemblies/${encodeURIComponent(assemblyId)}/attendance`,
      body
    );
  }
  attendanceList(assemblyId: string, kind: string, key: string) {
    return this.request<{ total: number; entries: { id: number; name: string; role: string; via: string; at: string; booth: string | null }[] }>(
      "GET",
      `/assemblies/${encodeURIComponent(assemblyId)}/attendance?kind=${encodeURIComponent(kind)}&key=${encodeURIComponent(key)}`
    );
  }

  /** A vehicle's driver link (?a=HAR_01&drive=<key>): the key is the only credential. */
  driveLogin(assemblyId: string, key: string): Promise<DriveLoginReply> {
    return this.request("POST", "/auth/drive", { assemblyId, key }, false);
  }
  drive(assemblyId: string): Promise<DriveInfo> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/drive`);
  }
  driveStart(assemblyId: string, clientId: string): Promise<{ session: DriveSession; resumed: boolean }> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/drive/sessions`, { clientId });
  }
  driveEnd(assemblyId: string, sessionId: number): Promise<{ session: DriveSession }> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/drive/sessions/${sessionId}/end`, {});
  }
  drivePoints(assemblyId: string, sessionId: number, points: DrivePoint[]): Promise<DrivePointsReply> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/drive/points`, { sessionId, points });
  }

  /* ---------------- वोट डाला (polling-day booth agent) ---------------- */
  /** the voter-list oath (agreed every 12 hours) before the booth's supporters open */
  voterOath(assemblyId: string): Promise<VoterOath> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/voter-oath`);
  }
  agreeOath(assemblyId: string, version: string): Promise<{ version: string; agreedAt: string | null }> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/voter-oath`, { agree: true, version });
  }
  voted(assemblyId: string, code: string): Promise<VotedDay> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/voted`);
  }
  markVoted(assemblyId: string, code: string, marks: { serial: number; via: "serial" | "qr" | "list" }[], undo = false): Promise<VotedReply> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/voted`, undo ? { marks, undo: true } : { marks });
  }

  /* ---------------- मतगणना (counting agent) ---------------- */
  counting(assemblyId: string): Promise<CountingInfo> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/counting`);
  }
  countBooth(assemblyId: string, code: string, round: number | null, votes: Record<string, number>): Promise<{ entry: { code: string; round: number | null; total: number; corrected: boolean } }> {
    return this.request("PUT", `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/count`, { round, votes });
  }

  async listAssemblies(): Promise<AssemblyInfo[]> {
    return (await this.request<{ assemblies: AssemblyInfo[] }>("GET", "/assemblies")).assemblies;
  }

  /** 201 first time; 200 with duplicate: true for a retry with the same clientId. */
  raiseSos(assemblyId: string, body: SosBody): Promise<SosReply> {
    return this.request("POST", `/assemblies/${encodeURIComponent(assemblyId)}/sos`, body);
  }

  /** The caller's own alerts of the last 3 days, with status and who handled them. */
  mySos(assemblyId: string): Promise<{ alerts: Sos[]; serverTime: string }> {
    return this.request("GET", `/assemblies/${encodeURIComponent(assemblyId)}/sos/mine`);
  }

  reportTurnout(assemblyId: string, code: string, body: TurnoutBody): Promise<{ turnout: Turnout }> {
    return this.request("PUT", `/assemblies/${encodeURIComponent(assemblyId)}/booths/${encodeURIComponent(code)}/turnout`, body);
  }
}

/** GET /assemblies/:aid/places — only the fields the field app shows */
export interface AreaPlaces {
  booths: { code: string; no: number; name: string; panchayat: string | null; places: number }[];
  places: { id: number; boothCode: string; boothName: string; kind: string; name: string; lat: number | null; lng: number | null; address?: string | null; densest?: boolean }[];
  summary: { booths: number; boothsWithPlaces: number; places: number; placesWithGps: number };
}
export type BoothPlaceKind = "market" | "chowk" | "crowd" | "ground" | "auditorium" | "hall" | "village" | "slum" | "dalit" | "tribal" | "dense" | "other";
export interface BoothPlaceInput {
  kind: BoothPlaceKind;
  name: string;
  address: string | null;
  population: number | null;
  households: number | null;
  lat: number | null;
  lng: number | null;
}
export interface BoothPlace extends BoothPlaceInput {
  id: number;
  boothCode: string;
  note: string | null;
}

export interface SurveySaved {
  clientId: string;
  id: number | null;
  flags: string[];
  duplicate: boolean;
}
export interface SurveyMine {
  total: number;
  today: number;
  d2d: number;
  tele: number;
  booths: number;
  recent: { id: number; name: string; status: "strong" | "neutral" | "weak"; method: "d2d" | "tele"; at: string; flags: string[]; place: string | null; booth: string }[];
}

export interface ExitPlanInfo {
  pollDate: string | null;
  method: string | null;
  sampling: string | null;
  targetPerBooth: number | null;
  windowFrom: string;
  windowTo: string;
  options: { key: string; label: string }[];
  issues: string[];
  saved: boolean;
}
/** one voter's answer — anonymous: no name, no phone */
export interface ExitAnswer {
  clientId: string;
  boothCode: string;
  collectedAt: string;
  choice: string;
  prevChoice?: string | null;
  ageGroup: "a18_25" | "a26_40" | "a41_60" | "a60plus";
  gender: "M" | "F" | "O";
  issue?: string | null;
  method: "face" | "phone";
  lat?: number | null;
  lng?: number | null;
  accuracyM?: number | null;
}


/* ---------------- वोट डाला ---------------- */
export interface VoterOath {
  version: string;
  text: { hi: string[]; en: string[] };
  agreedAt: string | null;
}
export interface PendingSupporter {
  serial: number;
  name: string;
  relativeName: string | null;
  relation: "father" | "husband" | "mother" | "other" | null;
  houseNo: string | null;
  age: number;
  gender: "M" | "F" | "O";
}
export interface VotedCounts {
  voters: number;
  voted: number;
  supporters: number;
  supportersVoted: number;
  /** supporters not marked yet, house by house */
  pending: PendingSupporter[];
  /** the last marks today, newest first */
  recent: { serial: number; name: string; at: string; via: string | null; supporter: boolean }[];
}
export interface VotedDay extends VotedCounts {
  date: string;
  booth: { code: string; name: string; panchayat: string | null };
}
export interface VotedReply extends VotedCounts {
  code: string;
  changed: { serial: number; name: string | null; supporter: boolean }[];
  already: { serial: number; name: string }[];
  unknown: number[];
}

/* ---------------- मतगणना ---------------- */
/** GET /counting as a counting agent sees it (an admin login gets the board; the fields used here are in both) */
export interface CountingInfo {
  setup: { countDate: string | null; rounds: number | null; candidates: { id: string; name: string; party: string | null }[] };
  booths: { code: string; name: string; entered?: boolean; votes?: Record<string, number> | null; round: number | null; enteredAt: string | null }[];
}
